import React, { useState, useRef, useEffect, useCallback } from "react";
import Context from "../Context";
import ReCAPTCHA from "react-google-recaptcha";
import {
  RiUser3Line,
  RiLockPasswordLine,
  RiEyeLine,
  RiEyeOffLine,
  RiAlertLine,
  RiTimeLine,
} from "react-icons/ri";
import { FiLogIn } from "react-icons/fi";
import { Spinner } from "../components/ui";

const MAX_FAILED_ATTEMPTS = 3;
const LOCKOUT_BASE_DURATION_SEC = 30; // ล็อก 30 วินาที
const STORAGE_FAILED_COUNT_KEY = "admin_login_failed_count";
const STORAGE_LOCKOUT_UNTIL_KEY = "admin_login_lockout_until";

export const Login = () => {
  const recaptchaRef = useRef(null);
  const { callApi } = Context();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);

  // Lockout / Cooldown state
  const [failedCount, setFailedCount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_FAILED_COUNT_KEY);
    return saved ? parseInt(saved, 10) || 0 : 0;
  });

  const [lockoutRemaining, setLockoutRemaining] = useState(0);

  // คำนวณเวลาล็อกที่เหลืออยู่
  const checkLockout = useCallback(() => {
    const lockoutUntil = localStorage.getItem(STORAGE_LOCKOUT_UNTIL_KEY);
    if (!lockoutUntil) {
      setLockoutRemaining(0);
      return 0;
    }

    const diffSec = Math.ceil((parseInt(lockoutUntil, 10) - Date.now()) / 1000);
    if (diffSec > 0) {
      setLockoutRemaining(diffSec);
      return diffSec;
    } else {
      localStorage.removeItem(STORAGE_LOCKOUT_UNTIL_KEY);
      setLockoutRemaining(0);
      return 0;
    }
  }, []);

  // Timer loop สำหรับ Countdown
  useEffect(() => {
    checkLockout();
    const interval = setInterval(() => {
      const remaining = checkLockout();
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [checkLockout]);

  const onReCaptchaChange = (recaptchaToken) => {
    setToken(recaptchaToken || "");
    if (errorMessage) setErrorMessage("");
  };

  const handleModifierCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    // หากยังอยู่ในช่วงติดคูลดาวน์/ล็อกเอาต์
    if (lockoutRemaining > 0) {
      setErrorMessage(`ระบบถูกระงับชั่วคราว กรุณารออีก ${lockoutRemaining} วินาที`);
      return;
    }

    if (!token) {
      setErrorMessage("กรุณายืนยันตัวตนผ่าน reCAPTCHA ก่อนเข้าสู่ระบบ");
      return;
    }
    if (!username.trim() || !password) {
      setErrorMessage("กรุณากรอกชื่อผู้ใช้และรหัสผ่านให้ครบถ้วน");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const result = await callApi("users-login", {
        username: username.trim(),
        password,
        reCaptchaToken: token,
        admin: true,
      });

      if (result && result.success) {
        // เคลียร์ประวัติการกรอกผิดเมื่อเข้าสู่ระบบสำเร็จ
        localStorage.removeItem(STORAGE_FAILED_COUNT_KEY);
        localStorage.removeItem(STORAGE_LOCKOUT_UNTIL_KEY);
        localStorage.setItem("accessToken", result.data.accessToken);
        window.location.href = "/";
      } else {
        localStorage.removeItem("accessToken");

        // อัปเดตจำนวนครั้งที่กรอกผิดจาก Backend หรือคำนวณในเครื่อง
        const currentFailedCount = result?.failedCount || failedCount + 1;
        setFailedCount(currentFailedCount);
        localStorage.setItem(STORAGE_FAILED_COUNT_KEY, currentFailedCount.toString());

        if (result?.locked || currentFailedCount >= MAX_FAILED_ATTEMPTS) {
          const durationSec = result?.lockoutRemainingSec || (currentFailedCount >= 5 ? 900 : LOCKOUT_BASE_DURATION_SEC);
          const lockoutTime = Date.now() + durationSec * 1000;
          localStorage.setItem(STORAGE_LOCKOUT_UNTIL_KEY, lockoutTime.toString());
          setLockoutRemaining(durationSec);
          setErrorMessage(
            result?.toastMessage ||
              result?.message ||
              `ระงับการเข้าสู่ระบบชั่วคราว ${Math.ceil(durationSec / 60)} นาที เนื่องจากกรอกรหัสผ่านผิดติดต่อกันครบ ${currentFailedCount} ครั้ง`
          );
        } else {
          const remainingAttempts = Math.max(0, 5 - currentFailedCount);
          setErrorMessage(
            result?.toastMessage ||
              result?.message ||
              `ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง (เหลือโอกาสอีก ${remainingAttempts} ครั้ง ก่อนถูกระงับชั่วคราว)`
          );
        }

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setToken("");
        }
      }
    } catch (err) {
      setErrorMessage("เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง");
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setToken("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isLocked = lockoutRemaining > 0;

  return (
    <div className="relative min-h-screen w-full bg-slate-100 flex items-center justify-center p-4 sm:p-6 overflow-hidden select-none">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 animate-in fade-in duration-300 select-none z-10">
          <img
            src="/logo.png"
            alt="วันดีลอตเตอรี่"
            className="w-32 h-auto object-contain drop-shadow-xs grayscale-[100%] opacity-10"
            onError={(e) => {
              e.target.src = "/images/HT_new_logo.png";
            }}
          />
          <p className="text-gray-300 font-semibold text-xs">บริษัท วันดี ออนไลน์ จำกัด</p>
          <Spinner className="w-6 h-6" />
          {/* <p className="text-xs font-semibold text-slate-500 tracking-wide">
            กำลังเข้าสู่ระบบ...
          </p> */}
        </div>
      ) : (
        <div className="relative z-10 w-full max-w-[420px] bg-white rounded-md border border-slate-200 shadow-sm p-5 flex flex-col gap-6">



          {/* Error / Lockout Alert Banner */}
          {isLocked ? (
            <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 border border-amber-200 rounded-md text-amber-900 text-sm font-semibold animate-pulse">
              <RiTimeLine className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold">ระบบถูกระงับชั่วคราว</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  กรุณารออีก <span className="font-bold text-amber-900 text-sm">{lockoutRemaining}</span> วินาที ก่อนลองใหม่อีกครั้ง
                </p>
              </div>
            </div>
          ) : errorMessage ? (
            <div className="flex items-start gap-2.5 p-3 bg-rose-50 border border-rose-200 rounded-md text-rose-700 text-sm font-semibold">
              <RiAlertLine className="w-5 h-5 shrink-0 mt-0.5 text-rose-500" />
              <span className="leading-snug">{errorMessage}</span>
            </div>
          ) : null}

          {/* Form */}
          <form onSubmit={handleLogin} className="flex flex-col gap-3">
            {/* Username Input */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="username"
                className="text-sm font-semibold text-slate-700"
              >
                บัญชีผู้ใช้ / เบอร์โทรศัพท์
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <RiUser3Line className="w-4 h-4" />
                </div>
                <input
                  id="username"
                  type="text"
                  disabled={isLocked}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  onKeyDown={handleModifierCheck}
                  onKeyUp={handleModifierCheck}
                  placeholder="กรอกชื่อผู้ใช้หรือเบอร์โทรศัพท์"
                  className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 rounded-md text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue disabled:bg-slate-100 disabled:cursor-not-allowed transition-colors"
                  autoComplete="username"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-sm font-semibold text-slate-700"
                >
                  รหัสผ่าน
                </label>
                {capsLockOn && (
                  <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                    ⚠️ Caps Lock เปิดอยู่
                  </span>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                  <RiLockPasswordLine className="w-4 h-4" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  disabled={isLocked}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errorMessage) setErrorMessage("");
                  }}
                  onKeyDown={handleModifierCheck}
                  onKeyUp={handleModifierCheck}
                  placeholder="กรอกรหัสผ่าน"
                  className="w-full pl-9 pr-10 py-2.5 bg-white border border-slate-300 rounded-md text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue disabled:bg-slate-100 disabled:cursor-not-allowed transition-colors"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 disabled:opacity-50 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <RiEyeOffLine className="w-4 h-4" />
                  ) : (
                    <RiEyeLine className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* ReCAPTCHA */}
            <div className="flex justify-center my-1">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LepvVYqAAAAAOZudU8U6HwAe1h2AFpxtQCx7R0i"
                onChange={onReCaptchaChange}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLocked || !token || isLoading || !username.trim() || !password}
              className="w-full py-2.5 px-4 text-sm font-bold rounded-md bg-blue hover:opacity-90 active:opacity-100 text-white transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isLocked ? (
                <>
                  <RiTimeLine className="w-4 h-4 animate-spin" />
                  <span>ลองใหม่ในอีก {lockoutRemaining} วินาที</span>
                </>
              ) : (
                <>
                  <FiLogIn className="w-4 h-4" />
                  <span>เข้าสู่ระบบ</span>
                </>
              )}
            </button>
          </form>

          {/* Security Footer */}
          <div className="flex border-t pt-4  items-center justify-center gap-1.5 text-xs text-slate-300 font-semibold">
            <p>บริษัท วันดี ออนไลน์ จำกัด</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
